/**
 * Per-session persistence for the tactics board's mutable state —
 * dragged player positions, drawn arrows, and the formation choice.
 * Shared by both the 3D board (TacticsBoard3D) and the 2D board
 * (TacticsBoard2D) so a layout drawn in one shows up in the other.
 *
 * Scoped to `4cm_local_tactics_3d` keyed by an opaque persistKey
 * (typically `tactics-3d-${sessionId}`). When the coach reloads the
 * /sessions/:id/3d (or /2d) page tomorrow, their arranged tokens +
 * sketched runs are still there.
 *
 * The legacy storage key is intentionally kept as `4cm_local_tactics_3d`
 * for back-compat — the 3D board shipped first. The shape is the
 * canonical `TacticsLayout` from `@4cm/core/tactics`; we re-export it
 * locally as `PersistedTactics` so existing call sites keep compiling.
 *
 * Storage shape is versioned so future schema changes can no-op stale
 * entries instead of crashing on a missing field.
 */

import {
  TACTICS_SCHEMA_VERSION,
  type TacticsLayout,
  type TacticsPlayerSpot,
} from '@4cm/core/tactics';

const STORE_KEY = '4cm_local_tactics_3d';
const SCHEMA_VERSION = TACTICS_SCHEMA_VERSION;

// Brief-affecting inputs — the slice of TacticsLayout the rule engine
// reads when producing PlayerBriefs. If any of these change between
// saves, the next save flips `playerBriefsStale = true` so the drawer
// prompts the coach to regenerate.
function sameSpots(
  a: TacticsPlayerSpot[] | undefined,
  b: TacticsPlayerSpot[] | undefined,
): boolean {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i]!;
    const bi = b[i]!;
    if (ai.role !== bi.role) return false;
    if (ai.x !== bi.x) return false;
    if (ai.z !== bi.z) return false;
  }
  return true;
}

function briefInputsDiffer(
  prev: TacticsLayout,
  next: Omit<TacticsLayout, 'v' | 'saved_at'>,
): boolean {
  if (prev.homeFormation !== next.homeFormation) return true;
  if (prev.awayFormation !== next.awayFormation) return true;
  if (!sameSpots(prev.homeSpots, next.homeSpots)) return true;
  if (!sameSpots(prev.awaySpots, next.awaySpots)) return true;
  // Arrows / teamTactics / playerRoles / setPieces — JSON.stringify is
  // cheap relative to the debounce interval and these are small shallow
  // objects. Key-order stability is guaranteed by the React setState
  // call sites that build them.
  if (JSON.stringify(prev.arrows ?? []) !== JSON.stringify(next.arrows ?? [])) return true;
  if (JSON.stringify(prev.teamTactics ?? null) !== JSON.stringify(next.teamTactics ?? null)) return true;
  if (JSON.stringify(prev.playerRoles ?? null) !== JSON.stringify(next.playerRoles ?? null)) return true;
  if (JSON.stringify(prev.setPieces ?? []) !== JSON.stringify(next.setPieces ?? [])) return true;
  return false;
}

/**
 * Back-compat alias. New code should prefer `TacticsLayout` from
 * `@4cm/core/tactics`; existing 3D-board imports keep working.
 */
export type PersistedTactics = TacticsLayout;

type Store = Record<string, TacticsLayout>;

function readStore(): Store {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORE_KEY) : null;
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch { return {}; }
}

function writeStore(store: Store): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch (err) {
    console.warn('[tactics3d store] could not persist', err);
  }
}

export function loadTacticsLayout(key: string): TacticsLayout | null {
  const store = readStore();
  const entry = store[key];
  if (!entry) return null;
  // Schema 1 → 5 are forward-compatible — every v=2..5 field is
  // optional, so an older entry loads cleanly into the latest type.
  // Newer entries from a future schema (v > current) are rejected so a
  // back-rev client doesn't silently drop unknown fields on write.
  if (typeof entry.v !== 'number' || entry.v > SCHEMA_VERSION) return null;
  return entry;
}

export function saveTacticsLayout(
  key: string,
  layout: Omit<TacticsLayout, 'v' | 'saved_at'>,
): TacticsLayout {
  const store = readStore();
  const prev = store[key];

  // Slot-preservation safety net — boards that don't manage a given
  // field must not drop it when they auto-save. If the caller omits a
  // preserved field but the prior entry had one, keep the prior.
  // Pass an explicit empty array / null to clear deliberately.
  //
  // Briefs (managed by the briefs drawer); keyframes / branches /
  // voiceovers / constraints (managed by Brain Bar sequence commits) —
  // none of those flow through the boards' debounced auto-save today,
  // so without this net every board save would wipe Brain-authored
  // multi-keyframe plays.
  const briefsFromCaller = 'playerBriefs' in layout ? layout.playerBriefs : undefined;
  const historyFromCaller = 'playerBriefHistory' in layout ? layout.playerBriefHistory : undefined;
  const keyframesFromCaller = 'keyframes' in layout ? layout.keyframes : undefined;
  const branchesFromCaller = 'branches' in layout ? layout.branches : undefined;
  const voiceoversFromCaller = 'voiceovers' in layout ? layout.voiceovers : undefined;
  const constraintsFromCaller = 'constraints' in layout ? layout.constraints : undefined;
  const rosterFromCaller = 'roster' in layout ? layout.roster : undefined;
  const nextBriefs = briefsFromCaller !== undefined ? briefsFromCaller : prev?.playerBriefs;
  const nextHistory = historyFromCaller !== undefined ? historyFromCaller : prev?.playerBriefHistory;
  const nextKeyframes = keyframesFromCaller !== undefined ? keyframesFromCaller : prev?.keyframes;
  const nextBranches = branchesFromCaller !== undefined ? branchesFromCaller : prev?.branches;
  const nextVoiceovers = voiceoversFromCaller !== undefined ? voiceoversFromCaller : prev?.voiceovers;
  const nextConstraints = constraintsFromCaller !== undefined ? constraintsFromCaller : prev?.constraints;
  const nextRoster = rosterFromCaller !== undefined ? rosterFromCaller : prev?.roster;

  // Auto-stale: if the previous entry had briefs and the brief-relevant
  // inputs differ on this save, flag stale. Honour an explicit
  // `playerBriefsStale` from the caller (the brief generator writes
  // `false` to clear the flag after a regen) — only auto-compute when
  // the caller didn't speak.
  let nextStale = layout.playerBriefsStale;
  if (nextStale === undefined && nextBriefs && Object.keys(nextBriefs).length > 0) {
    if (prev) {
      nextStale = briefInputsDiffer(prev, layout) || prev.playerBriefsStale === true;
    }
  }

  const entry: TacticsLayout = {
    ...layout,
    ...(nextBriefs !== undefined ? { playerBriefs: nextBriefs } : {}),
    ...(nextHistory !== undefined ? { playerBriefHistory: nextHistory } : {}),
    ...(nextStale === undefined ? {} : { playerBriefsStale: nextStale }),
    ...(nextKeyframes !== undefined ? { keyframes: nextKeyframes } : {}),
    ...(nextBranches !== undefined ? { branches: nextBranches } : {}),
    ...(nextVoiceovers !== undefined ? { voiceovers: nextVoiceovers } : {}),
    ...(nextConstraints !== undefined ? { constraints: nextConstraints } : {}),
    ...(nextRoster !== undefined ? { roster: nextRoster } : {}),
    v: SCHEMA_VERSION,
    saved_at: new Date().toISOString(),
  };
  store[key] = entry;
  writeStore(store);
  // Broadcast so live boards rehydrate when an external writer
  // (Brain commit, another tab) updates the layout. Without this
  // the board's in-flight state silently overwrites the new entry
  // on its next debounced save tick.
  emitLayoutChange(key);
  return entry;
}

export function clearTacticsLayout(key: string): void {
  const store = readStore();
  if (!(key in store)) return;
  delete store[key];
  writeStore(store);
  emitLayoutChange(key);
}

// ---------------------------------------------------------------------------
// Change notifications — for external writers (Brain commits, future
// multi-tab sync) so live boards can rehydrate without polling.
// ---------------------------------------------------------------------------

/** Custom event name dispatched on `window` whenever the store changes. */
export const TACTICS_LAYOUT_CHANGED = 'tactics-layout-changed';

export interface TacticsLayoutChangeEvent extends CustomEvent<{ key: string }> {
  type: typeof TACTICS_LAYOUT_CHANGED;
}

function emitLayoutChange(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(TACTICS_LAYOUT_CHANGED, { detail: { key } }),
    );
  } catch {
    // CustomEvent unsupported in some test envs — silently drop, the
    // store is still updated and a full reload would still rehydrate.
  }
}

/**
 * Subscribe to layout changes for a specific persistKey. The callback
 * fires after the new entry is in localStorage, so callers can
 * `loadTacticsLayout(key)` to read the fresh state.
 *
 * Returns an unsubscriber suitable for a `useEffect` cleanup.
 */
export function onTacticsLayoutChanged(
  key: string,
  cb: () => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (e: Event) => {
    const ce = e as TacticsLayoutChangeEvent;
    if (ce.detail?.key === key) cb();
  };
  window.addEventListener(TACTICS_LAYOUT_CHANGED, handler);
  return () => window.removeEventListener(TACTICS_LAYOUT_CHANGED, handler);
}
